import type { Command } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

import type { AgentManagedExtensionAttributes } from '../../../common/agentManagedExtensionPluginType';
import {
	findAgentManagedExtension,
	findAgentManagedExtensionByLocalId,
	getNodeAiManagedObject,
} from '../../../common/utils';
import {
	getAiManagedObjectWithCache,
	getCachedAiManagedObject,
	updateAiManagedObjectWithCache,
} from '../../ai-managed-object-cache';
import { setNodeAiManagedObject } from '../../editor-commands/agent-managed-object-node';
import {
	getAiManagedObjectLoadStatusFromState,
	getAutoUpdateIntervalTargetFromState,
	setMeta,
} from '../../pm-plugins/state';
import {
	type ManagedBlockUpdateInterval,
	toAiManagedObjectConfigInputWithUpdateInterval,
} from '../configuration';

import type { AutoUpdateIntervalOption } from './types';

type SelectedAiManagedObject = {
	aimoId?: string;
	cachedAimo?: AiManagedObject;
	localId?: string;
};

const getSelectedAiManagedObject = (editorView: EditorView): SelectedAiManagedObject => {
	const found = findAgentManagedExtension(editorView.state);
	const attrs = found?.node.attrs as AgentManagedExtensionAttributes | undefined;
	const aimoId = getNodeAiManagedObject(attrs)?.id;

	return {
		aimoId,
		cachedAimo: aimoId ? getCachedAiManagedObject(editorView, aimoId) : undefined,
		localId: found?.node.attrs.localId as string | undefined,
	};
};

const dispatchOpenAutoUpdateInterval = (
	editorView: EditorView,
	selected: SelectedAiManagedObject,
): boolean => {
	const currentTarget = getAutoUpdateIntervalTargetFromState(editorView.state);
	const isCurrentTargetOpen = currentTarget.isOpen && currentTarget.localId === selected.localId;

	if (isCurrentTargetOpen) {
		return false;
	}

	editorView.dispatch(
		setMeta(editorView.state.tr, {
			localId: selected.localId,
			type: 'OPEN_AUTO_UPDATE_INTERVAL' as const,
		}),
	);

	return true;
};

const getAiManagedObjectSelectionByLocalId = (
	editorView: EditorView,
	localId: string,
): SelectedAiManagedObject => {
	const selected = getSelectedAiManagedObject(editorView);
	if (selected.localId === localId) {
		return selected;
	}

	const found = findAgentManagedExtensionByLocalId(editorView.state, localId);
	const attrs = found?.node.attrs as AgentManagedExtensionAttributes | undefined;
	const aimoId = getNodeAiManagedObject(attrs)?.id;
	return {
		aimoId,
		cachedAimo: aimoId ? getCachedAiManagedObject(editorView, aimoId) : undefined,
		localId,
	};
};

const hydrateSelectionFromEditorView = async (
	editorView: EditorView,
	selected: SelectedAiManagedObject,
): Promise<void> => {
	if (!selected.aimoId) {
		return;
	}

	await getAiManagedObjectWithCache({
		aimoId: selected.aimoId,
		cachedAimo: selected.cachedAimo,
		editorView,
		localId: selected.localId,
	});
};

const persistUpdateInterval = async (
	editorView: EditorView,
	updateInterval?: ManagedBlockUpdateInterval,
): Promise<void> => {
	const target = getAutoUpdateIntervalTargetFromState(editorView.state);
	const selected = target.localId
		? getAiManagedObjectSelectionByLocalId(editorView, target.localId)
		: getSelectedAiManagedObject(editorView);
	if (!selected.aimoId) {
		return;
	}

	const aimo = await getAiManagedObjectWithCache({
		aimoId: selected.aimoId,
		cachedAimo: selected.cachedAimo,
		editorView,
		localId: selected.localId,
	});
	if (!aimo) {
		return;
	}

	const updatedAimo = await updateAiManagedObjectWithCache(
		editorView,
		{
			id: aimo.id,
			config: toAiManagedObjectConfigInputWithUpdateInterval(aimo.config, updateInterval),
		},
		selected.localId,
	);

	const latestTarget = getAutoUpdateIntervalTargetFromState(editorView.state);
	if (!latestTarget.isOpen || latestTarget.localId !== selected.localId) {
		return;
	}

	const tr = editorView.state.tr;
	setNodeAiManagedObject(editorView.state, tr, updatedAimo, selected.localId);
	setMeta(tr, { type: 'CLOSE_AUTO_UPDATE_INTERVAL' });
	editorView.dispatch(tr);
};

export const createUpdateIntervalCommand =
	(option: AutoUpdateIntervalOption): Command =>
	(_state, _dispatch, editorView?: EditorView) => {
		if (!editorView) {
			return false;
		}

		void persistUpdateInterval(editorView, option === 'none' ? undefined : option);
		return true;
	};

export const createHydrateSelectionCommand =
	(): Command => (_state, _dispatch, editorView?: EditorView) => {
		if (!editorView) {
			return false;
		}

		const selected = getSelectedAiManagedObject(editorView);
		const currentTarget = getAutoUpdateIntervalTargetFromState(editorView.state);
		const isCurrentTargetOpen = currentTarget.isOpen && currentTarget.localId === selected.localId;
		if (isCurrentTargetOpen) {
			const loadStatus = getAiManagedObjectLoadStatusFromState(editorView.state, selected.aimoId);
			return loadStatus === 'failed' || loadStatus === 'loading';
		}

		const isOpening = dispatchOpenAutoUpdateInterval(editorView, selected);
		if (isOpening) {
			void hydrateSelectionFromEditorView(editorView, selected).catch(() => undefined);
		}
		return isOpening;
	};

export const createRetryHydrateSelectionCommand =
	(): Command => (_state, _dispatch, editorView?: EditorView) => {
		if (!editorView) {
			return false;
		}

		const target = getAutoUpdateIntervalTargetFromState(editorView.state);
		const selected = target.localId
			? getAiManagedObjectSelectionByLocalId(editorView, target.localId)
			: getSelectedAiManagedObject(editorView);
		void hydrateSelectionFromEditorView(editorView, selected).catch(() => undefined);
		return true;
	};
